import React from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { ParsedUrlQuery } from 'querystring'
import { useQuery } from '@apollo/client'
import upperFirst from 'lodash.upperfirst'

import { useProductCategoryTaxonomyHooks } from 'hooks/useProductCategoryTaxonomyHooks'
import { useUserSession, getSessionAccountId } from 'hooks/useUserSession'
import { arrayParam } from 'lib/query-helper'

/* GraphQL */
import GET_DEPARTMENTS from 'modules/marketplace/common/graphql/GetDepartments.graphql'
import {
  GetDepartments,
  GetDepartments_departmentFullList,
  GetDepartments_departmentFullList_departmentChildren_child
} from 'modules/marketplace/common/graphql/__generated__/GetDepartments'

import GET_CATALOGS_FOR_SHOP_PAGE from './graphql/GetCatalogsForShopPage.graphql'
import {
  CatalogSortingEnum,
  ElasticsearchIndexEnum,
  SortDirectionEnum
} from '../../../../../../__generated__/globalTypes'
import { GetCatalogsForShopPage, GetCatalogsForShopPageVariables } from './graphql/__generated__/GetCatalogsForShopPage'

import GET_CURRENT_PROMOTIONS from './graphql/GetCurrentPromotions.graphql'
import { GetCurrentPromotions } from './graphql/__generated__/GetCurrentPromotions'

/* Components */
import Breadcrumbs from 'components/Breadcrumbs'
import Pagination from 'components/Pagination'
import ScrollToTop from 'components/ScrollToTop'
import FetchingPanel from 'components/FetchingPanel'
import { Robots, StructuredData } from 'components/SEO'
import ShopMainContainer from '../../components/ShopMainContainer'
import RetailerSignupBanner from 'modules/website/components/RetailerSignupBanner'

/* Marketplace Components */
import BasePage from 'modules/marketplace/layout/components/BasePage'
import { Container } from 'modules/marketplace/layout/components/ResponsiveContainers'
import DepartmentsMenuBar from 'modules/marketplace/header/components/DepartmentsMenuBar'

/* Module Components */
import CatalogListGrid from '../../components/CatalogListGrid'
import CatalogListHeader from '../../components/CatalogListHeader'
import SubDepartmentCards from '../../components/SubDepartmentCards'
import BannedBuyerAlertAlert from 'modules/buyer-hub/account/components/BannedBuyerAlert'

/* Module Styles */
import styles from './CatalogListPage.module.css'
function getCatalogQueryVariables({
  query,
  superDepartment,
  subDepartment,
  productCategoryTaxonomySlugs
}: {
  query: ParsedUrlQuery
  superDepartment?: GetDepartments_departmentFullList
  subDepartment?: GetDepartments_departmentFullList_departmentChildren_child
  productCategoryTaxonomySlugs?: string[]
}): GetCatalogsForShopPageVariables {
  const { freeShippingOver, minimumTotalOrderValue, sellerCountries, shippingCountries, topBrandValueIds, page } = query

  return {
    catalogFilter: {
      superDepartmentId: superDepartment?.id,
      departmentSlugs: subDepartment ? [subDepartment.slug] : undefined,
      productCategoryTaxonomySlugs: productCategoryTaxonomySlugs,
      shipsTo: shippingCountries ? (shippingCountries as unknown as string).split(',') : undefined,
      freeShippingOver: freeShippingOver ? 0 : undefined,
      minimumTotalOrderValue:
        minimumTotalOrderValue === undefined
          ? undefined
          : minimumTotalOrderValue === null || minimumTotalOrderValue === 'none'
          ? null
          : parseInt(minimumTotalOrderValue as unknown as string),
      sellerCountries: sellerCountries ? (sellerCountries as unknown as string).split(',') : undefined,
      topBrandValueIds: topBrandValueIds ? (arrayParam(topBrandValueIds) as string[]) : undefined
    },
    catalogSort: {
      sortBy: CatalogSortingEnum.DEFAULT,
      sortDirection: SortDirectionEnum.ASC
    },
    page: parseInt(page as unknown as string) ?? 1,
    perPage: 20
  }
}

const CatalogListPage = () => {
  const { isGuest } = useUserSession()
  const router = useRouter()
  const { getProductCategoryNameFromSlug } = useProductCategoryTaxonomyHooks()

  const departmentsQuery = useQuery<GetDepartments>(GET_DEPARTMENTS)
  const superDepartment = departmentsQuery?.data?.departmentFullList.find(
    d => d.slug === router.query.superDepartmentSlug
  )
  const subDepartment =
    superDepartment?.departmentChildren.find(d => d.child?.slug === router.query.subDepartmentSlug)?.child || undefined

  // Only pass the 'last' slug to the search query, as that's all that is required to identify the product category.
  //
  // This also means in the future if we want to support filtering by multiple categories, we can do that by passing multiple slugs
  const productCategoryTaxonomySlugs = arrayParam(router.query.productCategorySlug)
  const productCategoryTaxonomyLastSlug = productCategoryTaxonomySlugs?.slice(-1)

  const catalogQueryVariables = getCatalogQueryVariables({
    query: router.query,
    superDepartment,
    subDepartment,
    productCategoryTaxonomySlugs: productCategoryTaxonomyLastSlug
  })

  const isLoggedIn = Boolean(getSessionAccountId())

  const { loading, data } = useQuery<GetCatalogsForShopPage, GetCatalogsForShopPageVariables>(
    GET_CATALOGS_FOR_SHOP_PAGE,
    {
      variables: catalogQueryVariables,
      /*
       * NOTE: Use 'cache-first' to allow SSR - for logged-out users(GoogleBots),
       * otherwise, use default 'no-cache' to ensure the latest data is fetched for logged-in users
       */
      fetchPolicy: isLoggedIn ? 'no-cache' : 'cache-first'
    }
  )
  const catalogs = data?.marketplaceCatalogs.nodes
  const catalogsCount = data?.marketplaceCatalogs.nodesCount ?? 0

  const promotionsQuery = useQuery<GetCurrentPromotions>(GET_CURRENT_PROMOTIONS)
  let departmentFeature
  if (superDepartment?.slug && !Boolean(subDepartment?.slug)) {
    departmentFeature = promotionsQuery.data?.currentPromotions?.slots.nodes.find(
      promotionSlot => promotionSlot.promotion.department?.slug === superDepartment?.slug
    )
  }

  const breadcrumbs = [
    {
      label: 'Shop',
      href: '/shop'
    },
    {
      label: superDepartment?.name || '',
      href: `/shop/d/${superDepartment?.slug}`
    },
    {
      label: subDepartment?.name || '',
      href: `/shop/d/${superDepartment?.slug}/${subDepartment?.slug}`
    }
  ]

  return (
    <BasePage hasMarketingFooter={isGuest} hasGuestBanner={isGuest} hideIntercom>
      <Head>
        <title>{`Wholesale ${upperFirst(
          subDepartment?.slug || superDepartment?.slug || 'catalogs'
        )} - Fieldfolio`}</title>
        <link rel="canonical" href={`${process.env.NEXT_PUBLIC_MARKETPLACE_URL}${router.asPath}`} />
        {/* SEO Recommendation: If the subdepartment name matches the superdepartment (kids & fashion) noindex the subdepartment */}
        <Robots content={`follow, ${subDepartment?.name === superDepartment?.name ? 'noindex' : 'index'}`} />
      </Head>

      <DepartmentsMenuBar />

      <Container>
        <BannedBuyerAlertAlert className={styles.banBuyerContainer} />
      </Container>

      <Container className={styles.catalogsHeaderContainer}>
        <div className={styles.headerTitleContainer}>
          <CatalogListHeader
            departmentName={subDepartment ? subDepartment.name : superDepartment?.name}
            departmentImage={subDepartment ? `/marketplace/department-covers/${subDepartment.slug}.jpg` : ''}
            isSubDepartment={!!subDepartment}
          />
        </div>

        {!subDepartment && (
          <SubDepartmentCards
            className={styles.subDepartmentCards}
            superDepartmentSlug={superDepartment?.slug as string}
          />
        )}
      </Container>

      <ShopMainContainer productCategoryMode={[ElasticsearchIndexEnum.CATALOGS]}>
        {loading && <FetchingPanel />}
        {!loading && catalogs && catalogs.length > 0 && (
          <CatalogListGrid promotionSlot={departmentFeature} catalogs={catalogs} />
        )}
        {!loading && catalogsCount === 0 && (
          <div className={styles.noResultsContainer}>
            {productCategoryTaxonomyLastSlug && (
              <>No results for {getProductCategoryNameFromSlug(productCategoryTaxonomyLastSlug?.[0] || '')}</>
            )}
            {!productCategoryTaxonomyLastSlug && (
              <>No {subDepartment?.name || superDepartment?.name || ''} results found</>
            )}
          </div>
        )}

        {!loading && (
          <Pagination
            baseUrl={router.asPath}
            origin={process.env.NEXT_PUBLIC_MARKETPLACE_URL}
            thingName="catalogs"
            currentPage={catalogQueryVariables.page ? parseInt(catalogQueryVariables.page as unknown as string) : 1}
            totalPages={data?.marketplaceCatalogs.pagesCount ?? 1}
            showSummary={false}
            perPage={catalogQueryVariables.perPage ?? 20}
            className={styles.pagination}
            showPageStatus
            totalItems={data?.marketplaceCatalogs.nodesCount ?? 1}
          />
        )}
        <ScrollToTop iconPosition="left" className={styles.srollToTopButton} minScrollAmount={0} />
      </ShopMainContainer>

      <Container className={styles.breadcrumbsContainer}>
        <Breadcrumbs crumbs={breadcrumbs} hasHomeCrumb={false} />
      </Container>

      <StructuredData
        data={{
          '@context': 'http://schema.org',
          '@graph': [
            {
              '@type': 'WebSite',
              url: `${process.env.NEXT_PUBLIC_MARKETPLACE_URL}`
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: breadcrumbs
                .filter(crumb => !!crumb.label)
                .map((crumb, index) => {
                  return {
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                      '@id': `${process.env.NEXT_PUBLIC_MARKETPLACE_URL}${crumb.href}`,
                      name: crumb.label
                    }
                  }
                })
            },
            {
              '@type': 'ItemList',
              itemlistElement: catalogs?.map((catalog, index) => {
                return {
                  '@type': 'ListItem',
                  '@id': `${process.env.NEXT_PUBLIC_MARKETPLACE_URL}/${catalog.seller.slug}/${catalog.slug}`,
                  name: catalog.name,
                  position: index + 1,
                  url: `${process.env.NEXT_PUBLIC_MARKETPLACE_URL}/${catalog.seller.slug}/${catalog.slug}`,
                  image: catalog.photoMediumUrl
                }
              })
            }
          ]
        }}
      />
      {isGuest && <RetailerSignupBanner />}
    </BasePage>
  )
}

export default CatalogListPage
